import type { ApprovalRequest } from '@/lib/types';
import { BucketShell } from './_BucketShell';
import { readStructuredFields } from './_payload';

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function asList(value: unknown): string[] {
  if (Array.isArray(value)) return value.map((item) => String(item)).filter(Boolean);
  if (value === null || value === undefined || value === '') return [];
  return [String(value)];
}

export function GrnUnapprovedView({ req }: { req: ApprovalRequest }) {
  const sf = readStructuredFields(req);
  const staleRisk = asRecord(sf['stale_exact_grn_duplicate_risk']);
  const staleInvoices = Array.isArray(staleRisk['oracle_purchase_invoices'])
    ? (staleRisk['oracle_purchase_invoices'] as unknown[]).map(asRecord)
    : [];
  const staleInvoice = staleInvoices[0] ?? {};
  const staleDetailIds = asList(staleRisk['grn_detail_ids']);
  const staleGrnNos = asList(staleRisk['grn_nos']);
  const sameRefNetOnly =
    staleRisk['invoice_ref_matches'] === true &&
    staleRisk['invoice_net_matches_grn_set'] === true &&
    staleRisk['invoice_gross_matches_grn_set'] === false;
  if (staleDetailIds.length > 0 || staleGrnNos.length > 0) {
    return (
      <BucketShell req={req}>
        <section className="rounded-lg border border-error-300 bg-error-50 p-16">
          <p className="text-xs font-semibold uppercase tracking-wide text-error-800">
            Exact GRN already invoiced
          </p>
          <p className="mt-4 text-sm text-neutral-700">
            The matching GRN evidence is already linked to an Oracle purchase invoice.
          </p>
          {staleGrnNos.length > 0 && (
            <p className="mt-4 text-sm text-neutral-700">
              GRNs: {staleGrnNos.join(', ')}
            </p>
          )}
          {staleDetailIds.length > 0 && (
            <p className="mt-4 text-sm text-neutral-700">
              GRN details: {staleDetailIds.join(', ')}
            </p>
          )}
          <p className="mt-4 text-sm text-neutral-700">
            PI {String(staleInvoice['purchase_invoice_id'] ?? '—')}
            {staleInvoice['purchase_invoice_ref_no']
              ? ` / ref ${String(staleInvoice['purchase_invoice_ref_no'])}`
              : ''}
            {staleInvoice['purchase_invoice_date']
              ? ` / ${String(staleInvoice['purchase_invoice_date']).slice(0, 10)}`
              : ''}
          </p>
          {sameRefNetOnly && (
            <p className="mt-4 text-sm font-medium text-error-800">
              Oracle ref and net match this scan, but tax/gross differ.
            </p>
          )}
          <p className="mt-4 text-xs text-neutral-600">
            Treat this as duplicate-risk until Finance confirms whether the prior Oracle
            invoice ref or the current paperwork is wrong.
          </p>
        </section>
      </BucketShell>
    );
  }

  const candidates = Array.isArray(sf['unapproved_grn_candidates'])
    ? (sf['unapproved_grn_candidates'] as Record<string, unknown>[])
    : [];
  const grnNos = candidates
    .map((candidate) => String(candidate['grn_no'] ?? candidate['GRN_NO'] ?? '').trim())
    .filter(Boolean);

  return (
    <BucketShell req={req}>
      <section className="rounded-lg border border-amber-300 bg-amber-50 p-16">
        <p className="text-xs font-semibold uppercase tracking-wide text-amber-800">
          GRN awaiting approval
        </p>
        <p className="mt-4 text-sm text-neutral-700">
          Matching GRNs exist but are not approved in WizERP.
        </p>
        {grnNos.length > 0 && (
          <p className="mt-4 text-sm text-neutral-700">Blocked GRNs: {grnNos.join(', ')}</p>
        )}
        <p className="mt-4 text-xs text-neutral-600">
          Ask the relevant approvers to approve the GRN, then refresh candidates below.
        </p>
      </section>
    </BucketShell>
  );
}
